import { useScrollAnimation, fadeInUp, staggerContainer, AnimatedDiv, AnimatedH2 } from "@/hooks/useScrollAnimation";
import { Star, MessageSquare, ThumbsUp } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import neilImg480 from "@/assets/neil-gibbs-st-george-real-estate-agent-480w.webp";
import neilImg896 from "@/assets/neil-gibbs-st-george-real-estate-agent-896w.webp";

const SocialProof = () => {
  const { ref, controls } = useScrollAnimation();

  return (
    <section className="py-20 px-6 bg-background">
      <div className="container mx-auto max-w-7xl">
        <AnimatedDiv
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <AnimatedH2
            variants={fadeInUp}
            className="text-3xl md:text-4xl heading-luxury text-foreground mb-4"
          >
            Experience You Can Trust
          </AnimatedH2>
          <AnimatedDiv variants={fadeInUp}>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Decades of local knowledge, proven results, and guidance you can rely on when buying or selling in St. George.
            </p>
          </AnimatedDiv>
        </AnimatedDiv>

        {/* Featured Agent Card */}
        <AnimatedDiv
          variants={fadeInUp}
          className="mb-12"
        >
          <Card className="group relative overflow-hidden p-10 bg-white border border-border max-w-4xl mx-auto shadow-lg">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="w-48 h-48 md:w-60 md:h-60 flex-shrink-0">
                <img 
                  src={neilImg896}
                  srcSet={`${neilImg480} 480w, ${neilImg896} 896w`}
                  sizes="(min-width: 768px) 240px, 192px"
                  alt="Neil Gibbs - St. George Utah real estate broker and agent with 32 years experience"
                  className="w-full h-full object-cover object-top rounded-xl shadow-md"
                  loading="lazy"
                  decoding="async"
                  width={896}
                  height={1200}
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl font-bold text-foreground mb-1">Neil Gibbs</h3>
                <p className="text-primary font-semibold text-lg mb-4">Your Local St. George Real Estate Expert</p>
                
                <p className="text-foreground font-medium text-lg mb-4 tracking-wide">
                  32+ years. Local expertise. Results-driven.
                </p>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Neil Gibbs is a licensed Broker with decades of experience in the St. George market, specializing in residential, land, luxury, and commercial real estate. Known for hands-on service, strategic pricing, and powerful marketing, Neil works directly with every client to ensure a smooth, informed, and successful transaction.
                </p>
                
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <a 
                    href="mailto:namgibbs@gmail.com"
                    className="text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    namgibbs@gmail.com
                  </a>
                  <a href="/about-neil" aria-label="Learn more about Neil Gibbs, St. George real estate expert">
                    <Button className="bg-primary text-white hover:bg-primary/90">
                      About Neil Gibbs
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </AnimatedDiv>

        <AnimatedDiv
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {/* Google Reviews Card */}
          <AnimatedDiv variants={fadeInUp}>
            <Card className="group relative overflow-hidden p-8 text-center hover:shadow-lg transition-all duration-300 bg-white border-border h-full flex flex-col justify-between">
              <div className="relative z-10">
                <div className="inline-block p-4 bg-primary/10 group-hover:bg-primary/15 rounded-full mb-4 transition-all duration-300">
                  <Star className="h-8 w-8 text-primary fill-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Google Reviews</h3>
                <div className="flex justify-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6">
                  See what our clients say about their experience working with us
                </p>
              </div>
              <a 
                href="https://share.google/kQD7mLUtSJUA36aZ8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative z-10"
              >
                <Button className="w-full btn-luxury">
                  Read Reviews
                </Button>
              </a>
            </Card>
          </AnimatedDiv>

          {/* Facebook Card */}
          <AnimatedDiv variants={fadeInUp}>
            <Card className="group relative overflow-hidden p-8 text-center hover:shadow-lg transition-all duration-300 bg-white border-border h-full flex flex-col justify-between">
              <div className="relative z-10">
                <div className="inline-block p-4 bg-primary/10 group-hover:bg-primary/15 rounded-full mb-4 transition-all duration-300">
                  <ThumbsUp className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Facebook</h3>
                <p className="text-muted-foreground mb-6">
                  Follow us for the latest listings, market insights, and St. George community updates
                </p>
              </div>
              <a 
                href="https://www.facebook.com/Britishwine/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative z-10"
              >
                <Button className="w-full btn-luxury">
                  Follow on Facebook
                </Button>
              </a>
            </Card>
          </AnimatedDiv>

          {/* Instagram Card */}
          <AnimatedDiv variants={fadeInUp}>
            <Card className="group relative overflow-hidden p-8 text-center hover:shadow-lg transition-all duration-300 bg-white border-border h-full flex flex-col justify-between">
              <div className="relative z-10">
                <div className="inline-block p-4 bg-primary/10 group-hover:bg-primary/15 rounded-full mb-4 transition-all duration-300">
                  <MessageSquare className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Instagram</h3>
                <p className="text-muted-foreground mb-6">
                  Discover stunning properties and Southern Utah lifestyle through our curated feed
                </p>
              </div>
              <a 
                href="https://www.instagram.com/neilgibbs.realtor/?hl=en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative z-10"
              >
                <Button className="w-full btn-luxury">
                  Follow on Instagram
                </Button>
              </a>
            </Card>
          </AnimatedDiv>
        </AnimatedDiv>
      </div>
    </section>
  );
};

export default SocialProof;
